import axios from "axios";

import type { StateUpdatedEvent, CellStyle } from "ag-grid-community";

type ProgressStoreAccessor = () => {
  setLoading: (isLoading: boolean) => void;
};
let progressStoreAccessor: ProgressStoreAccessor | null = null;

export function registerProgressStore(accessor: ProgressStoreAccessor): void {
  progressStoreAccessor = accessor;
}

function getProgressStore() {
  return progressStoreAccessor?.();
}

type AuthStoreAccessor = () => {
  logout: (logoutMsg?: string) => Promise<void>,
  getSessionId: () => string;
};
let authStoreAccessor: AuthStoreAccessor | null = null;

export function registerAuthStore(accessor: AuthStoreAccessor): void {
  authStoreAccessor = accessor;
}

function getAuthStore() {
  return authStoreAccessor?.();
}

type SettingsStoreAccessor = () => {
  setGridState: (item: string, value: any, delay?: number) => void;
};
let settingsStoreAccessor: SettingsStoreAccessor | null = null;

export function registerSettingsStore(accessor: SettingsStoreAccessor): void {
  settingsStoreAccessor = accessor;
}

function getSettingsStore() {
  return settingsStoreAccessor?.();
}

type TabsStoreAccessor = () => {
  changeTab: (id?: string) => void;
};
let tabsStoreAccessor: TabsStoreAccessor | null = null;

export function registerTabsStore(accessor: TabsStoreAccessor): void {
  tabsStoreAccessor = accessor;
}

function getTabsStore() {
  return tabsStoreAccessor?.();
}

////////////////////////////////////////////////////////////////////////////////
// 상수
////////////////////////////////////////////////////////////////////////////////
export const DEFAULT_PAGE_SIZE = 1000;
export const DEFAULT_ERR_MSG = "일시적으로 오류가 발생했습니다.<br>잠시 후 다시 접속해 주시고<br>문제가 반복되면 관리자에게 연락해 주세요.";

////////////////////////////////////////////////////////////////////////////////
// type
////////////////////////////////////////////////////////////////////////////////
export type Params = {
  [key: string]: string | number
};

export type KMapBase<T> = {
  [key: string]: T | T[] | KMapBase<T> | KMapBase<T>[];
};

export type KMap = KMapBase<string|number|boolean|Function>;

export type KInfo<K extends string> = {
  [key in K]?: string | string[] | number | number[] | KInfo<K> | KInfo<K>[];
};

export type PageInfo = {
  totalCount: number;
  totalPage: number;
  page: number;
  pageSize: number;
};

////////////////////////////////////////////////////////////////////////////////
// alert, confirm
////////////////////////////////////////////////////////////////////////////////
async function _alert(msg: string): Promise<void> {
  msg = msg.replace(/<br>/g, "\r\n");
  if (msg) {
    await window.alert(msg);
  }
}

async function alert(msg: string): Promise<void> {
  return await _alert(msg);
};

async function _confirm(msg: string): Promise<boolean> {
  msg = msg.replace(/<br>/g, "\r\n");
  return window.confirm(msg);
}

async function confirm(msg: string): Promise<boolean> {
  return await _confirm(msg);
}

////////////////////////////////////////////////////////////////////////////////
// loading status
////////////////////////////////////////////////////////////////////////////////
let loadingCallCnt = 0;
let loading: any = null;
function showLoading(options: KMap): void {
  if (options.skipLoading) {
    return;
  }
  if (loadingCallCnt == 0) {
    // Loading bar 표시
    getProgressStore()?.setLoading(true);
  }
  ++loadingCallCnt;
}

function hideLoading(options: KMap): void {
  if (options.skipLoading) {
    return;
  }
  setTimeout(() => {
    --loadingCallCnt;
    if (loadingCallCnt <= 0) {
      // Loading bar 숨기기
      getProgressStore()?.setLoading(false);
    }
  }, 0);
}

function setLoadingStatus(_isLoading: boolean): void {
  // 사용안함
  return;
}

////////////////////////////////////////////////////////////////////////////////
// http request
////////////////////////////////////////////////////////////////////////////////
function _addAuthorization(config: any): void {
  config.headers["x-session-id"] = getAuthStore()?.getSessionId();
}

// null => 공백으로 변환
function _fixParams<T>(params: T): void {
  if (!params) {
    return;
  }

  (Object.keys(params) as Array<keyof T>).forEach(key => {
    if (params[key] === null) {
      (params as any)[key] = "";
    }
  });
}

function _makeAxiosConfig(reqOptions: KMap): KMap {
  const config: KMap = {
    headers: {
    },
  };

  if (reqOptions.responseType) {
    config.responseType = reqOptions.responseType;
  }

  return config;
}

function _makeUrlString<T>(url: string, reqData: T): string {
  if (!reqData) {
    return "";
  }

  const urlSearchParams = new URLSearchParams();
  (Object.keys(reqData) as Array<keyof T>).forEach((key) => {
    urlSearchParams.append(key as string, "" + reqData[key]);
  });

  if (url.indexOf("?") < 0) {
    return url + "?" + urlSearchParams.toString();
  } else {
    return url + "&" + urlSearchParams.toString();
  }
}

interface Error {
  code: string;
  message: string;
  errObj: any;
};

function _makeError(code: string, message: string, errObj: any): Error {
  return { code, message, errObj };
}

async function _sendGet<T>(url: string, reqData: T, reqOptions: KMap): Promise<any> {
  let isProc = false;
  try {
    const config = _makeAxiosConfig(reqOptions);
    _addAuthorization(config);

    let reqUrl = url;
    _fixParams(reqData);
    reqUrl = _makeUrlString(url, reqData);
    let res = null;
    try {
      res = await axios.get(reqUrl, config);
    } catch(e: any) {
      if (e?.response?.data?.errorCode == "301") {
        // access token의 expired인 경우 refresh후 재시도
        await axios.post("/oauth2/v1/refresh", {
          refreshToken: _getRefreshToken()
        });
        res = await axios.get(reqUrl, config);
      } else {
        throw e;
      }
    }
    isProc = true;
    return await _procResult(res, reqData, reqOptions);
  } catch(e) {
    if (isProc) {
      // _procResult에서 error가 발생한 경우, 그대로 전달
      throw e;
    }
    await _procError(e, reqData, reqOptions, {method: "sendGet", url, reqData, reqOptions});
    return {}; // _procErro에서 에러를 throw 하기 때문에 실행되지 않음
  }
}

async function _sendPost<T>(url: string, reqData: T, reqOptions: KMap): Promise<any> {
  let isProc = false;
  try {
    const config = _makeAxiosConfig(reqOptions);
    const headers = (config.headers as KMap);
    headers["Content-Type"] = "application/json; charset=utf-8";
    _addAuthorization(config);

    _fixParams(reqData);
    let res = null;
    try {
      res = await axios.post(url, reqData, config);
    } catch(e: any) {
      if (e?.response?.data?.errorCode == "301") {
        // access token의 expired인 경우 refresh후 재시도
        await axios.post("/oauth2/v1/refresh", {
          refreshToken: _getRefreshToken()
        });
        res = await axios.post(url, reqData, config);
      } else {
        throw e;
      }
    }
    isProc = true;
    return await _procResult(res, reqData, reqOptions);
  } catch(e) {
    if (isProc) {
      // _procResult에서 error가 발생한 경우, 그대로 전달
      throw e;
    }
    await _procError(e, reqData, reqOptions, {method: "sendPost", url, reqData, reqOptions});
    return {}; // _procErro에서 에러를 throw 하기 때문에 실행되지 않음
  }
}

async function _procResult<T>(res: any, reqData: T, reqOptions: KMap): Promise<any> {
  // file download인 경우
  if (res.data instanceof Blob || res.data instanceof ArrayBuffer) {
    return res.data;
  }

  let errMsg = null;
  if (res.data.message) {
    errMsg = res.data.message;
    await _alert(errMsg);
  }
  if (res.data.error) {
    if (!errMsg) {
      const error = res.data.error;
      const errors = error.fieldErrors ? error.fieldErrors : error.businessErrors;
      if (!errors) {
        await _alert(DEFAULT_ERR_MSG);
        throw _makeError("UNKNOWN", "", error);
      }
      let skipAlert = false;
      if (reqOptions.validationMsg) {
        const firstError = errors[0];
        const validationMsg = reqOptions.validationMsg as any[];
        const msgInfo = validationMsg.find(msgInfo => (msgInfo.id == firstError.target || new RegExp("^" + msgInfo.id + "$").test(firstError.target)) && msgInfo.type == firstError.reason);
        if (msgInfo) {
          skipAlert = !!msgInfo.skipAlert;
          if (typeof msgInfo.msg == "function") {
            errMsg = msgInfo.msg(reqData, firstError);
          } else {
            errMsg = msgInfo.msg;
          }
        }
      }
      if (!skipAlert) {
        await _alert(errMsg || DEFAULT_ERR_MSG);
      }
    }
    throw _makeError(res.data.code, errMsg, res.data.error);
  } else {
    return res.data.data;
  }
}

// SUCCESS: OK(200)
// INTERNAL_SERVER_ERROR: OK(200)
// BAD_REQUEST: BAD_REQUEST(400)
// BAD_REQUEST: METHOD_NOT_ALLOWED(405)
// VALIDATION_FAIL: BAD_REQUEST(400)
// UNAUTHORIZED_ACCESS: FORBIDDEN(403)
// INTERNAL_SERVER_ERROR: NOT_FOUND(404)
async function _procError<T>(e: any, reqData: T, reqOptions: KMap, _reqInfo: any): Promise<void> {
  const defError = {
    code: "",
    msg: "",
    error: {}
  };
  // FIXME 파일다운로드 에러시 처리 확인 필요
  const result = e.response?.data;
  if (!result) {
    await _alert(DEFAULT_ERR_MSG);
    throw _makeError("UNKNOWN", DEFAULT_ERR_MSG, e);
  }

  // Apigee Error 처리
  if (result.errorCode) {
    const httpStatus = e.response.status;
    // 재로그인 필요. 400 Bad Request, 401 Unauthorized, 403 Forbidden
    if (httpStatus == 400 || httpStatus == 401 || httpStatus == 403) {
      if (!reqOptions?.skipLogout) {
        await getAuthStore()?.logout("로그인이 필요합니다.");
      }
      throw _makeError("UNKNOWN", DEFAULT_ERR_MSG, e);
    }

    const errMsg = "일시적으로 오류가 발생했습니다.<br>잠시 후 다시 접속해 주시고<br>문제가 반복되면 관리자에게 연락해 주세요.<br>[" + result.errorCode + "] " + result.errorMessage;
    await _alert(errMsg);
    throw _makeError(result.errorCode, errMsg, e);
  }

  const errCode = result.code;
  const msgErrors = [
    { code: "BAD_REQUEST", msg: "잘못된 요청입니다." },
    { code: "VALIDATION_FAIL", msg: "입력된 데이터의 유효성 검증에 실패하였습니다." },
    { code: "INTERNAL_SERVER_ERROR", msg: "업무 예외가 발생하였습니다." },
  ];
  if (msgErrors.find(e => e.code == errCode)) {
    const error = result.error;
    const errors = error.fieldErrors ? error.fieldErrors : error.businessErrors;
    if (!errors) {
      await _alert(DEFAULT_ERR_MSG);
      throw _makeError("UNKNOWN", "", error);
    }
    let errMsg = null;
    let skipAlert = false;
    if (reqOptions.validationMsg) {
      const firstError = errors[0];
      const validationMsg = reqOptions.validationMsg as any[];
      const msgInfo = validationMsg.find(msgInfo => (msgInfo.id == firstError.target || new RegExp("^" + msgInfo.id + "$").test(firstError.target)) && msgInfo.type == firstError.reason);
      if (msgInfo) {
        skipAlert = !!msgInfo.skipAlert;
        if (typeof msgInfo.msg == "function") {
          errMsg = msgInfo.msg(reqData, firstError);
        } else {
          errMsg = msgInfo.msg;
        }
      }
    }
    if (!skipAlert) {
      await _alert(errMsg || DEFAULT_ERR_MSG);
    }
    throw _makeError(result.code, errMsg, result.error);
  }

  const authErrors = [
    { code: "UNAUTHORIZED_ACCESS", msg: "로그인이 필요합니다." },
    { code: "FORBIDDEN", msg: "권한이 없습니다." },
  ];
  const authError = authErrors.find(e => e.code == errCode);
  if (authError) {
    await _alert(authError.msg);
    removeRefreshToken();
    if (authError.code == "FORBIDDEN") {
      getTabsStore()?.changeTab();
    }
    window.location.replace(window.location.href);
    throw _makeError("NO_AUTH", "", e);
  }

  const unknownErrors = [
  ];
  await _alert(DEFAULT_ERR_MSG);
  throw _makeError("UNKNOWN", "", e);
}

async function sendGet<T>(url: string, reqData: T, reqOptions: KMap): Promise<any> {
  try {
    showLoading(reqOptions);
    return await _sendGet(url, reqData, reqOptions);
  } finally {
    hideLoading(reqOptions);
  }
}

async function sendPost<T>(url: string, reqData: T, reqOptions: KMap): Promise<any> {
  try {
    showLoading(reqOptions);
    return await _sendPost(url, reqData, reqOptions);
  } finally {
    hideLoading(reqOptions);
  }
}

interface FileInfo {
  action: string;
  inputFieldName: string;
  fileId: string;
  file: Blob;
  filenameOrg: string;
}
async function uploadFile<T>(url: string, reqData: T, fileInfos: FileInfo[], reqOptions: KMap): Promise<any> {
  let isProc = false;
  try {
    showLoading(reqOptions);
    const formData = new FormData()
    const updateFormData = function(formData: FormData, fileInfos: FileInfo[]) {
      fileInfos.forEach(fileInfo => {
        if (fileInfo.file && (fileInfo.action == "ADD" || fileInfo.action == "UPDATE")) {
          formData.append(fileInfo.inputFieldName + "_" + fileInfo.action + "_" + fileInfo.fileId, fileInfo.file, fileInfo.filenameOrg);
        }
      });
    };
    updateFormData(formData, fileInfos);
    formData.append("data", JSON.stringify(reqData));

    const config = _makeAxiosConfig(reqOptions);
    (config.headers as KMap)["Content-Type"] = "multipart/form-data";
    _addAuthorization(config);

    let res = null;
    try {
      res = await axios.post(url, formData, config);
    } catch(e: any) {
      if (e?.response?.data?.errorCode == "301") {
        // access token의 expired인 경우 refresh후 재시도
        await axios.post("/oauth2/v1/refresh", {
          refreshToken: _getRefreshToken()
        });
        res = await axios.post(url, formData, config);
      } else {
        throw e;
      }
    }
    isProc = true;
    return await _procResult(res, reqData, reqOptions);
  } catch(e) {
    if (isProc) {
      // _procResult에서 error가 발생한 경우, 그대로 전달
      throw e;
    }
    await _procError(e, reqData, reqOptions, {method: "uploadFile", url, reqData, reqOptions});
    return {}; // _procErro에서 에러를 throw 하기 때문에 실행되지 않음
  } finally {
    hideLoading(reqOptions);
  }
}

async function downloadFile<T>(url: string, reqData: T, reqOptions: KMap): Promise<void> {
  try {
    showLoading(reqOptions);
    const options: KMap = copyObj(reqOptions, {});
    options.responseType = "blob";
    const file = await _sendGet(url, reqData, options);
    const link = document.createElement("a");
    link.href = URL.createObjectURL(file);
    link.download = options.fileName as string;
    link.click();
    URL.revokeObjectURL(link.href);
  } finally {
    hideLoading(reqOptions);
  }
}

////////////////////////////////////////////////////////////////////////////////
// validation
////////////////////////////////////////////////////////////////////////////////
async function checkValidation<T>(targetInfo: T, validationMsg: any[], excepts?: any[]): Promise<boolean> {
  if (!targetInfo) {
    return false;
  }
  const info = targetInfo as Record<string, any>;

  let isValid = true;
  let errMsg;
  for (const msgInfo of validationMsg) {
    if (!msgInfo) {
      continue;
    }
    if (excepts && excepts.find(e => e == msgInfo.id + "." + msgInfo.type)) {
      continue;
    } else if (typeof msgInfo.msg === "function") {
      const msg = msgInfo.msg(info);
      if (msg) {
        errMsg = msg;
        isValid = false;
        break;
      }
    } else if (msgInfo.type == "NotNull" || msgInfo.type == "NotBlank") {
      if (msgInfo.id.indexOf("(") < 0 && ((info[msgInfo.id] == null || !("" + info[msgInfo.id])))) {
        errMsg = msgInfo.msg;
        isValid = false;
        break;
      }
    }
  }
  if (isValid == false && errMsg) {
    await _alert(errMsg);
  }
  return isValid;
}

async function checkValidation4list<T>(targetInfos: T[], validationMsg: any[]): Promise<boolean> {
  let isValid = true;
  for (const info of targetInfos) {
    if (isValid) {
      isValid = await checkValidation<T>(info, validationMsg);
    }
  }
  return isValid;
}

async function checkNotBlank<T>(value: T, errMsg: string): Promise<boolean> {
  if (value) {
    return false;
  }
  await _alert(errMsg);
  return true;
}

////////////////////////////////////////////////////////////////////////////////
// object handling
////////////////////////////////////////////////////////////////////////////////
function deepCopy<T>(srcObj: T): T {
  return JSON.parse(JSON.stringify(srcObj));
}

function copyObj<S, T>(aSrcObj: S, aTargetObj: T, excepts?: any[]): T {
  const srcObj = aSrcObj as Record<string, any>;
  const targetObj = aTargetObj as Record<string, any>;

  const keys = Object.keys(srcObj);
  keys.forEach(key => {
    if (!excepts || excepts.findIndex(e => e == key) < 0) {
      targetObj[key] = srcObj[key];
    }
  });
  return aTargetObj;
}

function removeObj<T>(aObj: T, exceptObj: any): void {
  const obj = aObj as Record<string, any>;
  const keys = Object.keys(obj);
  keys.forEach(key => {
    if (!exceptObj) {
      delete obj[key];
    } else {
      const except = Object.keys(exceptObj).find(e => e == key);
      if (except == key) {
        obj[key] = exceptObj[key];
      } else {
        delete obj[key];
      }
    }
  });
}

////////////////////////////////////////////////////////////////////////////////
// convert data type
////////////////////////////////////////////////////////////////////////////////
function toInt(n: string | number, d: number): number {
  const v = parseInt(("" + n).replace(/,/g, ""), 10);
  if (isNaN(v)) {
    return d;
  } else {
    return v;
  }
}

function toDouble(n: string | number, d: number): number {
  const v = parseFloat(("" + n).replace(/,/g, ""));
  if (isNaN(v)) {
    return d;
  } else {
    return v;
  }
}

function string2date(str: string): Date|null {
  if (!str || str.length < 8) {
    return null;
  }

  let date;
  if (str.length < 14) {
    date = new Date(
      toInt(str.substr(0, 4), 0),
      toInt(str.substr(4, 2), 0) - 1,
      toInt(str.substr(6, 2), 0)
    );
  } else {
    date = new Date(
      toInt(str.substr(0, 4), 0),
      toInt(str.substr(4, 2), 0) - 1,
      toInt(str.substr(6, 2), 0),
      toInt(str.substr(8, 2), 0),
      toInt(str.substr(10, 2), 0),
      toInt(str.substr(12, 2), 0)
    );
  }
  return date;
}

function time2date(t: string|number|Date): Date {
  return new Date(t);
}

function date2string(rd: string|number|Date, f: string): string {
  if (!rd) {
    return "";
  }

  try {
    let tmpD: Date|null = null;
    if (typeof rd === "string") {
      tmpD = string2date(rd.trim());
    } else if (typeof rd === "number") {
      tmpD = time2date(rd);
    } else {
      tmpD = rd;
    }
    if (tmpD == null) {
      return "";
    }
    const d = tmpD;

    let h;
    const weekName = ["일", "월", "화", "수", "목", "금", "토"];

    return f.replace(/(yyyy|yy|MM|dd|E|HH|hh|mm|ss|SSS|M|d|a\/p)/g, function($1) {
      switch ($1) {
        case "yyyy": return "" + d.getFullYear();
        case "yy": return ("" + (d.getFullYear() % 100)).padStart(2, "0");
        case "MM": return ("" + (d.getMonth() + 1)).padStart(2, "0");
        case "M": return ("" + (d.getMonth() + 1));
        case "dd": return ("" + d.getDate()).padStart(2, "0");
        case "d": return ("" + d.getDate());
        case "E": return weekName[d.getDay()];
        case "HH": return ("" + d.getHours()).padStart(2, "0");
        case "hh": return ("" + ((h = d.getHours() % 12) != 0 ? h : 12)).padStart(2, "0");
        case "mm": return ("" + d.getMinutes()).padStart(2, "0");
        case "ss": return ("" + d.getSeconds()).padStart(2, "0");
        case "SSS": return ("" + d.getMilliseconds()).padStart(3, "0");
        case "a/p": return d.getHours() < 12 ? "AM" : "PM";
        default: return $1;
      }
    });
  } catch(e) {
    return "";
  }
}

function time2string(d: string, f: string): string {
  if (!d) {
    return "";
  }

  return f.replace(/(HH|hh|mm)/g, function($1) {
    switch ($1) {
      case "HH": return ("" + d.substr(0, 2));
      case "hh": return ("" + d.substr(0, 2));
      case "mm": return ("" + d.substr(2, 2));
      default: return $1;
    }
  });
}

async function blob2object(blob: Blob): Promise<object> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = function() {
      try {
        resolve(JSON.parse((reader.result as string) ?? ""));
      } catch(e) {
        reject(e);
      }
    };
    reader.onabort = function(e) {
      reject(e);
    };
    reader.onerror = function(e) {
      reject(e);
    }
    reader.readAsText(blob);
  });
}

function setComma(n: string): string {
  if (n == null) {
    return "";
  }
  
  n += "";
  var x = n.split(".");
  var x1 = x[0].replace(/,/g, "");
  var x2 = x.length > 1 ? "." + x[1] : "";
  x1 = x1.replace(/^(0+)(\d+)/, "$2");
  var rgx = /(\d+)(\d{3})/;
  while (rgx.test(x1)) {
      x1 = x1.replace(rgx, "$1" + "," + "$2");
  }
  return x1 + x2;
}

function cr2br(val: string): string {
  if (val) {
    return val.replace(/\r?\n/g, "<br>");
  } else {
    return val;
  }
}

function joinCheckBoxArr(csv: string, value: string, checked: boolean): string {
  const set = new Set(csv ? csv.split(",") : []);
  if (checked) {
    set.add(value);
  } else {
    set.delete(value);
  }
  return [...set].join(",");
}

////////////////////////////////////////////////////////////////////////////////
// 날짜 관련
////////////////////////////////////////////////////////////////////////////////
function addDate(date: string, days: number, format: string): string {
  const d = string2date(date);
  if (d == null) {
    return "";
  }
  d.setDate(d.getDate() + days);
  return date2string(d, format);
}

function addMonth(date: string, months: number, format: string): string {
  if (months == null || months == undefined) {
    return "";
  }
  const d = string2date(date);
  if (d == null) {
    return "";
  }
  d.setMonth(d.getMonth() + months);
  return date2string(d, format);
}

function getCurrKstDate() {
  const currDate = new Date();
  const utcTime = currDate.getTime() + (currDate.getTimezoneOffset() * 60 * 1000);
  const kstDate = new Date(utcTime + 9 * 60 * 60 * 1000);
  return kstDate;
}

////////////////////////////////////////////////////////////////////////////////
// local storate 관련
////////////////////////////////////////////////////////////////////////////////
function getCurrLocalDate(): Date {
  return new Date();
}

function setLocalData(key: string, val: string): void {
  window.localStorage[key] = val;
}

function removeLocalData(key: string): void {
  window.localStorage.removeItem(key);
}

function getLocalData(key: string): string {
  return window.localStorage[key] || "";
}

////////////////////////////////////////////////////////////////////////////////
// common util
////////////////////////////////////////////////////////////////////////////////
function initSearchParams(searchParams: any, defaultObj: any): void {
  Object.keys(searchParams).forEach(key => {
    if (key == "pageSize") {
      searchParams[key] = DEFAULT_PAGE_SIZE;
    } else if (defaultObj[key]) {
      searchParams[key] = defaultObj[key];
    } else {
      searchParams[key] = "";
    }
  });
}

function initSearchResult(result: any): void {
  result.totalCount = 0;
  result.totalPage = 0;
  result.currPage = 0;
  result.infos = [];
  result.searchParams = [];
}

function updateInfosNo(infos: any[], options?: any): void {
  const field = options?.field || "no";
  infos.forEach((info, idx) => {
    info[field] = idx + 1;
  });
}

let isRunning = false;
async function runWithoutDup(func: Function): Promise<void> {
  if (isRunning) {
    return;
  }

  try {
    isRunning = true;
    await func();
  } finally {
    isRunning = false;
  }
}

async function sleep(millis: number): Promise<number> {
  return new Promise((resolve, _reject) => {
    setTimeout(() => {
      resolve(0);
    }, millis);
  });
}

////////////////////////////////////////////////////////////////////////////////
// aggrid
////////////////////////////////////////////////////////////////////////////////
const STATE_PERSIST_SOURCES = ["columnOrder", "columnSizing", "columnVisibility", "columnPinning"]; // "sort", "filter"
const defaultColDef = {resizable: true, sortable: true};
const aggridButtonCellStyle: CellStyle = {display: "flex", justifyContent: "center", alignItems: "center"};

function _isCtrlC(e: any): boolean {
  return e.ctrlKey && e.altKey == false && e.shiftKey == false && e.keyCode == 67;
}

async function onCellCopy(e: any): Promise<void> {
  if (e.colDef?.context?.copyable || false == false) {
    return;
  }
  if (e.event && _isCtrlC(e.event)) {
    if (e.colDef?.valueFormatter && !e.colDef?.valueGetter) {
      window.navigator.clipboard.writeText(e.colDef.valueFormatter(e)).then(() => {});
    } else {
      window.navigator.clipboard.writeText(e.value).then(() => {});
    }
  }
}

async function onStateUpdated(e: StateUpdatedEvent, locationPath: string) {
  if (!locationPath) return;
  if (!e.sources.some((s) => STATE_PERSIST_SOURCES.includes(s))) return;

  const state = Object.fromEntries(
    STATE_PERSIST_SOURCES.map((name) => [name, (e.state as any)[name]])
  );
  getSettingsStore()?.setGridState(locationPath, state);
}

const onKeyDownForInput = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>, callback: Function): void => {
  if (e.key === "Enter") {
    callback(e);
  }
};

////////////////////////////////////////////////////////////////////////////////
// location
////////////////////////////////////////////////////////////////////////////////
const LOCATION_RANGE = {
  startWp: 6,
  level1: 7,
  level2: 2,
  level3: { wb: 45, wp: 18 },
  level4: { wb: 76, wp: 15 },
}


////////////////////////////////////////////////////////////////////////////////
// barcode scan
////////////////////////////////////////////////////////////////////////////////
type BarcodeScanHandler = (code: string) => Promise<void>;
interface BarcodeScanData {
  isScanning: boolean;
  scannedData: string;
  handleBarcode: BarcodeScanHandler | null;
}

const barcodeScanData: BarcodeScanData = {
  isScanning: false,
  scannedData: "",
  handleBarcode: null,
}

const onBarcodeScan = (e: KeyboardEvent) => {
  // Pause 수신 → 스캔 시작
  if (e.key === "Pause") {
    barcodeScanData.isScanning = true;
    barcodeScanData.scannedData = "";
    e.preventDefault();
    e.stopPropagation();
    return;
  }

  if (!barcodeScanData.isScanning) return; // 일반 타이핑은 그대로 통과

  e.preventDefault();
  e.stopPropagation();

  if (e.key === "Enter") {
    barcodeScanData.isScanning = false;
    barcodeScanData.handleBarcode?.(barcodeScanData.scannedData);
    barcodeScanData.scannedData = "";
    return;
  }

  if (e.key.length === 1) {
    barcodeScanData.scannedData += e.key;
  }
};

const startBarcodeScan = (callback: BarcodeScanHandler) => {
  if (barcodeScanData.handleBarcode) {
    stopBarcodeScan(callback);
  }
  barcodeScanData.handleBarcode = callback;
  window.addEventListener("keydown", onBarcodeScan, { capture: true });
};

const stopBarcodeScan = (_callback: BarcodeScanHandler) => {
  barcodeScanData.isScanning = false;
  barcodeScanData.scannedData = "";
  barcodeScanData.handleBarcode = null;
  window.removeEventListener("keydown", onBarcodeScan, { capture: true });
}

////////////////////////////////////////////////////////////////////////////////
// keyboard 단축키
////////////////////////////////////////////////////////////////////////////////
type KeyboardShortcutHandler = (code: string) => Promise<void>;
export interface KeyboardShortcut {
  id: string,
  altKey: boolean,
  ctrlKey: boolean,
  shiftKey: boolean,
  key: string
}

interface KeyboardShortcutData {
  shortcuts: KeyboardShortcut[],
  handleKeyboardShortcut: KeyboardShortcutHandler | null;
}

const keyboardShortcutData: KeyboardShortcutData = {
  shortcuts: [],
  handleKeyboardShortcut: null,
}

const onKeyboardShortcut = (e: KeyboardEvent) => {
  for (const shortcut of keyboardShortcutData.shortcuts) {
    if (e.altKey === shortcut.altKey
      && e.ctrlKey === shortcut.ctrlKey
      && e.shiftKey === shortcut.shiftKey
      && e.key.toLowerCase() === shortcut.key.toLowerCase()
    ) {
      e.preventDefault();
      e.stopPropagation();
      keyboardShortcutData.handleKeyboardShortcut?.(shortcut.id);
      return;
    }
  }
};

const startKeyboardShortcut = (shortcuts: KeyboardShortcut[], callback: BarcodeScanHandler) => {
  keyboardShortcutData.shortcuts = shortcuts;
  keyboardShortcutData.handleKeyboardShortcut = callback;
  window.addEventListener("keydown", onKeyboardShortcut, { capture: true });
};

const stopKeyboardShortcut = (_callback: BarcodeScanHandler) => {
  keyboardShortcutData.shortcuts = [];
  keyboardShortcutData.handleKeyboardShortcut = null;
  window.removeEventListener("keydown", onKeyboardShortcut, { capture: true });
}

////////////////////////////////////////////////////////////////////////////////
// refresh token
////////////////////////////////////////////////////////////////////////////////
interface RefreshToken {
  value: string;
  expiredAt: number;
}

const refreshTokenKey = "refresh-token";
const refreshTokenExpiredKey = "refresh-token-expired-at";
const getRefreshToken = async () => {
  try {
    const currRefreshToken = _getRefreshToken();
    if (currRefreshToken) {
      return true;
    }

    const {data} = await axios.get<RefreshToken>("/oauth2/v1/refresh");
    const refreshToken = data?.value;
    const expiredAt = data?.expiredAt;
    setLocalData(refreshTokenKey, refreshToken);
    setLocalData(refreshTokenExpiredKey, expiredAt.toString());
    return true;
  } catch(e) {
    console.log(e);

    // 진행중인 요청이 있다면 취소처리
    const before = Date.now();
    for(var i=0; i<25; ++i) {
      window.stop();
      await sleep(0);
    }
    const d = 300 - (Date.now() - before);
    await sleep(d < 0 ? 0 : d); // 300ms + a만큼 대기

    // 새로고침
    removeRefreshToken();
    window.location.replace(window.location.href);

    return new Promise<boolean>(() => {});
  }
};

const _getRefreshToken = () => getLocalData(refreshTokenKey);
const _getRefreshTokenExpireAt = () => Number(getLocalData(refreshTokenExpiredKey) ?? "");
const removeRefreshToken = () => {
    removeLocalData(refreshTokenKey);
    removeLocalData(refreshTokenExpiredKey);
};

////////////////////////////////////////////////////////////////////////////////
// clipboard
////////////////////////////////////////////////////////////////////////////////
async function writeClipboard(text: string): Promise<boolean> {
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
    }
  }

  const ta = document.createElement("textarea");
  ta.value = text;
  ta.style.position = "fixed";
  ta.style.opacity = "0";
  document.body.appendChild(ta);
  ta.select();
  try {
    return document.execCommand("copy");
  } finally {
    document.body.removeChild(ta);
  }
}

export default {
  DEFAULT_PAGE_SIZE,
  DEFAULT_ERR_MSG,

  // alert, confirm
  alert,
  confirm,

  // loading status
  showLoading,
  hideLoading,
  setLoadingStatus,

  // http request
  // makeUrlString,
  sendGet,
  sendPost,
  uploadFile,
  downloadFile,

  // validation
  checkValidation,
  checkValidation4list,
  checkNotBlank,

  // object 변환
  deepCopy,
  copyObj,
  removeObj,
  toInt,
  toDouble,
  string2date,
  time2date,
  date2string,
  time2string,
  blob2object,
  setComma,
  cr2br,
  joinCheckBoxArr,

  // 날짜 관련
  addDate,
  addMonth,
  getCurrKstDate,
  getCurrLocalDate,

  // local storate 관련
  setLocalData,
  removeLocalData,
  getLocalData,

  // common util
  initSearchParams,
  initSearchResult,
  updateInfosNo,
  runWithoutDup,
  sleep,

  // aggrid
  STATE_PERSIST_SOURCES,
  defaultColDef,
  aggridButtonCellStyle,
  onCellCopy,
  onStateUpdated,

  // react
  onKeyDownForInput,

  // location
  LOCATION_RANGE,

  // barcode scan
  startBarcodeScan,
  stopBarcodeScan,

  // keyboard 단축키
  startKeyboardShortcut,
  stopKeyboardShortcut,

  // refresh token
  getRefreshToken,
  removeRefreshToken,

  // clipboard
  writeClipboard,
}
